<template>
  <!-- Layout exclusivo para Login (pantalla completa sin sidebars ni headers) -->
  <div v-if="usePublicLayout" class="public-layout-shell w-screen">
    <router-view></router-view>
  </div>

  <!-- Layout para el resto de la aplicación (Dashboard) -->
  <div v-else class="grid grid-cols-2-small app-screen overflow-hidden" :class="{ 'grid-cols-2-menu': activeParent }">
    <!-- header del sistema -->
    <Nav />
    <main class="app-main-shell overflow-hidden flex flex-col" :class="{ 'dashboard-bg': route.name === 'HomePage' }">
      <HeaderHome @actualizar-propiedad="activeParent = $event" />
      <!-- espacio donde se va a rederisar todos los componentes -->
      <router-view class="app-router-shell flex-1 min-h-0 overflow-y-auto pl-4 pr-6 pt-5 pb-8"> </router-view>
    </main>
  </div>

  <!-- Overlay móvil: cierra el menú al tocar fuera -->
  <div v-if="!usePublicLayout && activeParent" class="fixed inset-0 bg-black/40 z-40 md:hidden"
    @click="activeParent = false"></div>

  <div id="container-pdf"></div>

  <!-- Componente PWA para instalación inteligente - DEBE estar fuera del grid -->
  <InstallPWA @installed="handlePWAInstalled" @dismissed="handlePWADismissed" />
</template>

<script>
import Nav from "./components/Nav/Nav.vue";
import HeaderHome from "./components/Nav/Header.vue";
import InstallPWA from "./components/PublicComponents/InstallPWA.vue";
import PWADetector from "./utils/pwa/pwaDetector";
import { computed } from "vue";
import { useRoute } from 'vue-router'
export default {
  name: "App",
  components: {
    Nav,
    HeaderHome,
    InstallPWA,
  },
  data() {
    return {
      activeParent: false,
      viewportSyncHandler: null,
    };
  },
  setup() {
    const route = useRoute()
    const usePublicLayout = computed(() => route.name === 'login' || route.meta?.publicLayout)
    return { route, usePublicLayout }
  },
  methods: {
    handlePWAInstalled() {
      console.log('PWA instalada exitosamente');
    },
    handlePWADismissed() {
      console.log('Usuario rechazó instalar PWA');
    },
    syncViewportHeight() {
      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      document.documentElement.style.setProperty(
        "--app-height",
        `${Math.round(viewportHeight)}px`
      );
    },
    syncDeviceClasses() {
      const body = document.body;
      const html = document.documentElement;
      const deviceInfo = PWADetector.getDeviceInfo();

      body.classList.toggle("ios-device", deviceInfo.os === "ios");
      body.classList.toggle("ios-standalone", deviceInfo.os === "ios" && deviceInfo.isPWAMode);
      body.classList.toggle("touch-device", deviceInfo.isMobile || deviceInfo.isTablet);
      html.classList.toggle("ios-device", deviceInfo.os === "ios");
      html.classList.toggle("ios-standalone", deviceInfo.os === "ios" && deviceInfo.isPWAMode);
    },
    setupViewportSync() {
      const handler = () => {
        this.syncViewportHeight();
        this.syncDeviceClasses();
      };

      this.viewportSyncHandler = handler;
      handler();
      window.addEventListener("resize", handler, { passive: true });
      window.addEventListener("orientationchange", handler, { passive: true });

      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", handler, { passive: true });
      }
    }
  },
  mounted() {
    this.setupViewportSync();
  },
  beforeUnmount() {
    if (!this.viewportSyncHandler) {
      return;
    }

    window.removeEventListener("resize", this.viewportSyncHandler);
    window.removeEventListener("orientationchange", this.viewportSyncHandler);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.viewportSyncHandler);
    }
  }
};
</script>
<style>
:root {
  --app-height: 100dvh;
}

html,
body,
#app {
  min-height: 100%;
  height: 100%;
}

body {
  overscroll-behavior: none;
  -webkit-text-size-adjust: 100%;
  background-color: #f8fafc;
}

.app-screen {
  height: var(--app-height, 100dvh);
  min-height: var(--app-height, 100dvh);
}

.app-main-shell {
  height: var(--app-height, 100dvh);
  min-height: 0;
}

.public-layout-shell {
  min-height: var(--app-height, 100dvh);
  height: auto;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.app-router-shell {
  padding-bottom: max(2rem, env(safe-area-inset-bottom));
}

.ios-standalone body {
  background-color: #f8fafc;
}

.grid-cols-2-small {
  grid-template-columns: 255px 1fr;
}

.grid-cols-2-small.grid-cols-2-menu {
  grid-template-columns: 70px 1fr;
  transition: all 0.25s;
}

.grid-cols-2-small.grid-cols-2-menu .small-hidden {
  display: none;
}

.grid-cols-2-small.grid-cols-2-menu .small-center {
  justify-content: center;
}

@media screen and (max-width: 768px) {

  /* En móvil el layout es 1 columna; el nav se vuelve overlay */
  .grid-cols-2-small {
    grid-template-columns: 1fr;
    transition: all 0.25s;
  }

  .grid-cols-2-small>nav {
    position: fixed;
    top: 0;
    left: 0;
    width: min(86vw, 320px);
    min-width: min(86vw, 320px);
    height: var(--app-height, 100dvh);
    min-height: var(--app-height, 100dvh);
    transform: translateX(-100%);
    transition: transform 0.25s;
    z-index: 50;
  }

  .grid-cols-2-small.grid-cols-2-menu>nav {
    transform: translateX(0);
  }

  .grid-cols-2-small .small-hidden {
    display: none;
  }

  .grid-cols-2-small .small-center {
    justify-content: center;
  }

  .grid-cols-2-small.grid-cols-2-menu {
    grid-template-columns: 1fr;
    transition: all 0.25s;
  }

  .grid-cols-2-small.grid-cols-2-menu .small-hidden {
    display: inline-flex;
  }

  .grid-cols-2-small.grid-cols-2-menu .small-center {
    justify-content: start;
  }
}

/* Eliminar las flechas del input number */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: none;
}

.dashboard-bg {
  background-color: #fafdff;
}

.select {
  background-color: #008cff !important;
  color: #ffffff;
}
</style>
